/// <amd-module name="Wit/DeliveryPlans/Board/TimelineUtils" />

import { addDaysToDate, daysInSpan } from "../Common/Utils/DateUtils";
import { BoardConstants } from "./BoardConstants";

/**
 * Scroll the view left by the given number of days.
 * @param gridRef
 * @param daysAdded
 * @param scale
 */
export function scrollTimeline(gridRef: HTMLDivElement, daysAdded: number, scale: number) {
    const addedWidth = getPixelWidthFromDays(daysAdded, scale);
    gridRef.scrollLeft += addedWidth;
}

/**
 * Scroll the view so that visibleStartDate is the first visible date.
 * @param gridRef
 * @param visibleStartDate
 * @param renderedStartDate
 * @param scale
 * @param spacerColumns
 */
export function scrollTimelineToDate(gridRef: HTMLDivElement, visibleStartDate: Date, renderedStartDate: Date, scale: number, spacerColumns: Date[]) {
    // TODO: should this take into account the team headers?
    gridRef.scrollLeft = getOffsetOfDate(visibleStartDate, renderedStartDate, scale, spacerColumns);
}

/**
 * The number of days that are currently visible
 * @param gridRef
 * @param scale
 */
export function getDaysInViewport(gridRef: HTMLDivElement, scale: number) {
    return getTimelineViewportWidth(gridRef.offsetWidth) / getDayWidth(scale);
}

export function getDaysFromPixels(pixels: number, scale: number) {
    return pixels / getDayWidth(scale);
}

export function getTimelineViewportWidth(offsetWidth: number) {
    return offsetWidth - BoardConstants.teamHeaderWidth;
}

export function getPixelWidthFromDays(days: number, scale: number) {
    return days * getDayWidth(scale);
}

export function getScrollDistanceInDays(gridRef: HTMLDivElement, scale: number) {
    return Math.round(getTimelineViewportWidth(gridRef.offsetWidth) / getDayWidth(scale) / 2);
}

/**
 * Return the first visible date
 * @param gridRef
 * @param renderedStartDate
 * @param scale
 * @param spacerColumns
 */
export function getVisibleStartDate(gridRef: HTMLDivElement, renderedStartDate: Date, scale: number, spacerColumns: Date[]) {
    return getDateAtOffset(gridRef.scrollLeft, renderedStartDate, scale, spacerColumns);
}

/**
 * Given an offset in pixels from the start of the timeline, return the Date at that offset
 * @param offset
 * @param renderedStartDate
 * @param scale
 * @param spacerColumns
 */
function getDateAtOffset(offset: number, renderedStartDate: Date, scale: number, spacerColumns: Date[]): Date {
    // first, find all the spacer columns before the offset
    let spacerSum = 0;
    const dayWidth = getDayWidth(scale);
    const spacerWidth = getSpacerWidth(scale);
    for (const spacerDate of spacerColumns) {
        if (daysInSpan(renderedStartDate, spacerDate) * dayWidth + spacerSum > offset) {
            break;
        }
        spacerSum += spacerWidth;
    }
    return addDaysToDate(renderedStartDate, getDaysFromPixels(offset - spacerSum, scale));
}

function getOffsetOfDate(date: Date, renderedStartDate: Date, scale: number, spacerColumns: Date[]): number {
    // sum the spacer columns before the date
    let spacerSum = 0;
    const spacerWidth = getSpacerWidth(scale);
    for (const spacerDate of spacerColumns) {
        if (spacerDate > date) {
            break;
        }
        spacerSum += spacerWidth;
    }
    return spacerSum + getPixelWidthFromDays(daysInSpan(renderedStartDate, date), scale);
}

/**
 * Determine if the grid calendar is loaded by checking if anything other than
 * the team headers have been rendered
 */
export function isGridCalendarInitialized(gridRef: HTMLDivElement) {
    return gridRef.offsetWidth > BoardConstants.teamHeaderWidth;
}

export function getDayWidth(scale: number) {
    return scale * BoardConstants.dayPixelWidth;
}

export function getSpacerWidth(scale: number) {
    return scale * BoardConstants.spacerPixelWidth;
}
